import React, { useEffect, useState } from 'react';
import styles from './enrichment-sources.module.sass';
import { Typography, Tooltip, Table, Col, Row, Button, Spin } from 'antd';
import Tag from '../../../../shared-components/Tag';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ProgressBar from '../../../../shared-components/ProgressBar';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import { InfoCircleFilled, LoadingOutlined } from '@ant-design/icons';
import SPLogo from '../../../../assets/sp-logo.svg';
import moment from 'moment-timezone';
import { STATUS } from '../../../../redux/constants';
import { Link } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

function EnrichmentSources({ oldData, statusOld, type, history, props }) {
  const { Title, Text } = Typography;
  const feedContainingIoc = oldData?.feeds_containing_ioc;
  const [reportMappingArray, setReportMappingArray] = useState([]);

  function handleSourceClick(uuid, state) {
    if (state === 'FEED') {
      props.updateTag(null);
      props.clearFilterProfile();
      history.push('/?feed=' + uuid);
    } else if (state === 'COLLECTION') {
      props.updateTag(null);
      props.clearFilterProfile();
      history.push('/?feed=' + uuid + '&feed_state=COLLECTION');
    }
  }

  useEffect(() => {
    props.resetEnrichmentFeedHasBlogsState();
  }, []);

  useEffect(() => {
    if (statusOld === STATUS.LOADED) {
      if (feedContainingIoc?.length > 0) {
        let feedUUIDs = '';

        for (let i in feedContainingIoc) {
          //for every live threat feed assemble a uuid's into a comma separated string
          if (i == feedContainingIoc.length - 1) {
            feedUUIDs = feedUUIDs + feedContainingIoc[i]?.uuid;
          } else {
            feedUUIDs = feedUUIDs + feedContainingIoc[i]?.uuid + ',';
          }
        }

        props.checkEnrichmentFeedHasBlogs(feedUUIDs);
      }
    }
  }, [statusOld]);

  const columns = [
    {
      title: (
        <>
          <ColumnTitle columnName="Name" />{' '}
          <Tooltip title="The name of the live threat feed">
            <InfoCircleFilled className={styles.info_circle} />
          </Tooltip>
        </>
      ),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <ColumnTitle columnName="Type" />,
      dataIndex: 'type',
      key: 'type',
      width: 150,
    },
    {
      title: (
        <>
          <ColumnTitle columnName="Feed Tags" />{' '}
          <Tooltip title="Tags added to the live threat feed">
            <InfoCircleFilled className={styles.info_circle} />
          </Tooltip>
        </>
      ),
      dataIndex: 'feed',
      key: 'feed',
      render: (value, record) => {
        let retArr = [];
        for (let i in value) {
          retArr.push(
            <Tag
              tag={{ name: value?.[i] }}
              canClick={
                props.mySubscriptionPlan?.has_access_to_threat_ranking
                  ? true
                  : false
              }
              isTagManuallyCreated={false}
              canRemove={false}
              extraParams={
                record?.type?.props?.children === 'COLLECTION'
                  ? '&feed_state=COLLECTION'
                  : ''
              }
              filterProfileToBeCleared={true}
              showTooltip={
                !props.mySubscriptionPlan?.has_access_to_threat_ranking
              }
              tooltipText={
                <>
                  Filtering on tags is not part of this subscription plan. To
                  upgrade your subscription contact us at{' '}
                  <a target="_blank" href="mailto:sales@silentpush.com">
                    sales@silentpush.com
                  </a>
                  .
                </>
              }
            />,
          );
        }
        return retArr?.length > 0 ? retArr : <Text strong>-</Text>;
      },
    },
    {
      title: (
        <>
          <ColumnTitle columnName="Collected Tags" />{' '}
          <Tooltip title="Individual observable’s tags collected through file or the URL when feed was created">
            <InfoCircleFilled className={styles.info_circle} />
          </Tooltip>
        </>
      ),
      dataIndex: 'collected',
      key: 'collected',
      render: (value, record) => {
        let retArr = [];
        for (let i in value) {
          retArr.push(
            <Tag
              tag={{ name: value?.[i] }}
              tagColor={'volcano'}
              canClick={
                props.mySubscriptionPlan?.has_access_to_threat_ranking
                  ? true
                  : false
              }
              isTagManuallyCreated={false}
              canRemove={false}
              extraParams={
                record?.type?.props?.children === 'COLLECTION'
                  ? '&feed_state=COLLECTION'
                  : ''
              }
              filterProfileToBeCleared={true}
              showTooltip={
                !props.mySubscriptionPlan?.has_access_to_threat_ranking
              }
              tooltipText={
                <>
                  Filtering on tags is not part of this subscription plan. To
                  upgrade your subscription contact us at{' '}
                  <a target="_blank" href="mailto:sales@silentpush.com">
                    sales@silentpush.com
                  </a>
                  .
                </>
              }
            />,
          );
        }
        return retArr?.length > 0 ? retArr : <Text strong>-</Text>;
      },
    },
    {
      title: (
        <>
          <ColumnTitle
            columnName={
              type === 'domain' ? 'Live Threat Feeds Score Value' : 'Value'
            }
          />{' '}
          <Tooltip title="A score calculated by live threat feed attributes (that you see in threat ranking details or enriched data)">
            <InfoCircleFilled className={styles.info_circle} />
          </Tooltip>
        </>
      ),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: (
        <>
          <ColumnTitle columnName="Score" />{' '}
          <Tooltip title="A live threat feeds score can be added during the creation of the live threat feeds, or any time the user edits a live threat feed">
            <InfoCircleFilled className={styles.info_circle} />
          </Tooltip>
        </>
      ),
      dataIndex: 'progress',
      key: 'progress',
    },
    {
      title: (
        <>
          <ColumnTitle columnName="TLP Amber Report" />
        </>
      ),
      dataIndex: 'report',
      key: 'report',
    },
  ];

  function assembleSources() {
    if (statusOld === 'loaded') {
      return feedContainingIoc?.map((feed, index) => {
        const feedTooltip = (
          <Row gutter={24}>
            <Col xs={5}>
              <div
                style={{
                  width: '2.5rem',
                  height: '3rem',
                  display: 'inline-block',
                  background: `url('${SPLogo}') no-repeat left top`,
                  backgroundSize: '12rem',
                  marginLeft: '.5rem',
                }}
              />
            </Col>
            <Col xs={19}>
              <Title level={5}>Silent Push Live Threat Feed Attributes</Title>
            </Col>
            <Col xs={24} style={{ marginLeft: '.5rem', marginTop: '.5rem' }}>
              <Text style={{ display: 'block' }}>
                Live Threat Feed Name:{' '}
                <Text strong>{feed?.name ? feed?.name : '-'}</Text>
              </Text>
              <Text style={{ display: 'block' }}>
                Vendor: <Text strong>{feed?.vendor ? feed?.vendor : '-'}</Text>
              </Text>
              <Text style={{ display: 'block', marginBottom: '.5rem' }}>
                Last Updated:{' '}
                <Text strong>
                  {feed?.last_updated
                    ? moment(feed?.last_updated)?.format('YYYY-MM-DD HH:mm')
                    : '-'}
                </Text>
              </Text>
              <Text style={{ display: 'block' }}>
                Frequency:
                <Text strong> {feed?.frequency ? feed?.frequency : '-'}</Text>
                <Text>{feed?.frequency <= 1 ? ' hour' : ' hours'}</Text>
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    className={styles.test}
                    bgcolor={feed?.frequency_score}
                    width={'72'}
                    completed={feed?.frequency_score}
                  />
                </div>
              </Text>
              <Text style={{ display: 'block' }}>
                Accuracy:{' '}
                <Text strong>
                  {feed?.accuracy_score ? feed?.accuracy_score : '-'}
                </Text>{' '}
                <Text>score</Text>
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    bgcolor={feed?.accuracy_score}
                    width={'72'}
                    completed={feed?.accuracy_score}
                  />
                </div>
              </Text>
              <Text style={{ display: 'block' }}>
                False Positive:{' '}
                <Text strong>
                  {feed?.false_positive_ratio
                    ? feed?.false_positive_ratio
                    : '-'}
                </Text>{' '}
                <Text>{feed?.false_positive_ratio ? 'FPs' : null}</Text>
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    bgcolor={feed?.false_positive_total}
                    width={'72'}
                    completed={feed?.false_positive_total}
                  />
                </div>
              </Text>
              <Text style={{ display: 'block' }}>
                GEO Spread:{' '}
                <Text strong>
                  {feed?.geographic_spread_score
                    ? feed?.geographic_spread_score
                    : '-'}
                </Text>{' '}
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    bgcolor={feed?.geographic_spread_score}
                    width={'72'}
                    completed={feed?.geographic_spread_score}
                  />
                </div>
              </Text>
              <Text style={{ display: 'block' }}>
                Overlap:{' '}
                <Text strong>
                  {feed?.overlap_score ? feed?.overlap_score : '-'}
                </Text>{' '}
                <Text>{feed?.overlap_score ? '%' : null}</Text>
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    bgcolor={feed?.overlap_score}
                    width={'72'}
                    completed={feed?.overlap_score}
                  />
                </div>
              </Text>
              <Text style={{ display: 'block' }}>
                Originator:{' '}
                <Text strong>
                  {feed?.originator_score ? feed?.originator_score : '-'}
                </Text>{' '}
                <Text>{feed?.originator_score ? '%' : null}</Text>
                <div
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '.5rem',
                  }}
                >
                  <ProgressBar
                    bgcolor={feed?.originator_score}
                    width={'72'}
                    completed={feed?.originator_score}
                  />
                </div>
              </Text>
            </Col>
          </Row>
        );

        //@FIXME there is little reason to not use the data directly from props.
        //By creating this data structure it's difficult to refer to its values anymore due to the <Text> wrappers since that turns them into a React object.
        //If this is done, the above reference in columns where we get the value from the type/state needs to be rewritten

        return {
          key: index + 1,
          name: (
            <>
              <a onClick={() => handleSourceClick(feed?.uuid, feed?.state)}>
                {feed?.name}
              </a>{' '}
              <Tooltip title={feedTooltip} placement="right">
                <InfoCircleFilled />
              </Tooltip>
            </>
          ),
          type: <Text strong>{feed?.state}</Text>,
          feed: feed?.feeds_tags,
          collected: feed?.global_tags,
          value: <Text strong>{feed?.total_feed_score} </Text>,
          progress: (
            <ProgressBar
              bgcolor={feed?.total_feed_score}
              width={'72'}
              completed={feed?.total_feed_score}
            />
          ),
          report:
            props?.enrichedIOCFeedBlogsStatus === STATUS.LOADING ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : props?.enrichedIOCFeedBlogs?.[index]?.reports > 0 ? (
              props?.mySubscriptionPlan?.has_access_to_tlp_amber_report ? (
                <Link to={`/blog?name=${feed?.uuid}`}>
                  <Button
                    type="link"
                    onClick={() => {
                      mixpanel.track('Report Viewed');
                    }}
                  >
                    View Report
                  </Button>
                </Link>
              ) : (
                <Tooltip
                  placement="bottom"
                  title={
                    <>
                      TLP Amber Reports is not part of this subscription plan.
                      To upgrade your subscription contact us at{' '}
                      <a target="_blank" href="mailto:sales@silentpush.com">
                        sales@silentpush.com
                      </a>
                      .
                    </>
                  }
                >
                  <Button
                    disabled={
                      !props?.mySubscriptionPlan?.has_access_to_tlp_amber_report
                    }
                    type="link"
                  >
                    View Report
                  </Button>
                </Tooltip>
              )
            ) : (
              <Text strong>-</Text>
            ),
        };
      });
    }
  }

  function returnTableData({ loading, iocType }) {
    if (loading) {
      return [
        {
          key: 100,
          name: <LoadingSkeleton />,
          type: <LoadingSkeleton />,
          feed: null,
          collected: null,
          value: <LoadingSkeleton />,
          progress: <LoadingSkeleton />,
        },
        {
          key: 101,
          name: <LoadingSkeleton />,
          type: <LoadingSkeleton />,
          feed: null,
          collected: null,
          value: <LoadingSkeleton />,
          progress: <LoadingSkeleton />,
        },
        {
          key: 102,
          name: <LoadingSkeleton />,
          type: <LoadingSkeleton />,
          feed: null,
          collected: null,
          value: <LoadingSkeleton />,
          progress: <LoadingSkeleton />,
        },
      ];
    } else {
      let sources = assembleSources();

      if (sources?.length > 0) {
        return sources;
      }
    }
  }

  return (
    <div className={styles.enrichment_sources} id="enrichment-sources">
      <div className={styles.title}>
        <Title level={4}>List of Live Threat Feeds</Title>
        <Text>
          List of live threat feeds where the observable currently exists.{' '}
        </Text>
      </div>

      {!feedContainingIoc?.length > 0 && (
        <Text className={styles.empty_state}>
          The Live Threat Feeds score is not available since this IoC is not
          part of any of available feeds.
        </Text>
      )}
      {feedContainingIoc?.length > 0 && (
        <Table
          columns={columns}
          dataSource={returnTableData({
            loading: statusOld !== 'loaded',
            iocType: type,
          })}
          pagination={false}
        />
      )}
    </div>
  );
}

export default EnrichmentSources;
